import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { OutlineXIcon } from '@tempmee/react-icons';
import { RouteLink } from '../../../commonComponents/RouteLink';
import { Spinner } from '../../../commonComponents/Spinner';
import Styles from './OfficeReviewsTable.module.scss';

/**
 * The table's frame: header labels, `<col>` widths, and the column count the
 * status rows span.
 *
 * Order is the Figma frame's, left to right. Body cells are written out longhand
 * below rather than driven from a `render` per column — nearly every office cell
 * is bespoke (a link, a tri-state label, an icon button, two wrapping cells), so
 * a render function per column would just be JSX with a layer of indirection.
 * What this list is for is keeping the header and the widths in one place and
 * deriving `colSpan` rather than hardcoding 9.
 */
const COLUMNS = [
  { key: 'shift', label: 'Shift', className: Styles.colShift },
  { key: 'shiftDate', label: 'Shift Date', className: Styles.colDate },
  { key: 'createdDate', label: 'Created Date', className: Styles.colDate },
  { key: 'name', label: 'Name', className: Styles.colName },
  { key: 'rating', label: 'Rating', className: Styles.colRating },
  { key: 'wouldWorkHereAgain', label: 'Would work here again?', className: Styles.colWorkAgain },
  { key: 'officeFeedback', label: 'Office Feedback', className: Styles.colFeedback },
  { key: 'privateFeedback', label: 'Private Feedback', className: Styles.colFeedback },
  { key: 'remove', label: 'Remove', className: Styles.colRemove },
];

/**
 * A row spanning the whole table, for the states that replace or follow the
 * rows rather than sitting in a column.
 */
const StatusRow = ({ children }) => (
  <TableRow>
    <TableCell colSpan={COLUMNS.length} align="center">
      {children}
    </TableCell>
  </TableRow>
);

StatusRow.propTypes = { children: PropTypes.node.isRequired };

/**
 * The office Reviews table.
 *
 * Purely presentational: rows arrive already mapped and already labelled by
 * `domain/OfficeReviews`' adapter, so nothing here formats a date, resolves a
 * null or decides whether a review is removable. Same contract as
 * `Campaigns/CampaignsTable.jsx`.
 *
 * Header cells take no `Typography` and no colour class on purpose — a
 * `TableCell` inside `TableHead` already renders a bold `<th scope="col">` at
 * 16px in `--text/primary` with a `--divider` bottom border, which is the whole
 * Figma header spec.
 */
export const OfficeReviewsTable = ({
  reviews,
  isFetchingPage,
  hasFailedPage,
  onRetry,
  onRemove,
}) => (
  <TableContainer>
    <Table size="medium" className={Styles.table}>
      <colgroup>
        {COLUMNS.map((column) => (
          <col key={column.key} className={column.className} />
        ))}
      </colgroup>

      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableCell key={column.key}>{column.label}</TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {reviews.map((review) => (
          <TableRow key={review.id} hover className={Styles.bodyRow}>
            <TableCell className={Styles.wrappingRowCell}>
              <RouteLink to={`/job/${review.jobId}`}>View Job</RouteLink>
            </TableCell>

            <TableCell className={Styles.wrappingRowCell}>
              <Typography variant="body2">{review.shiftDateLabel}</Typography>
            </TableCell>

            <TableCell className={Styles.wrappingRowCell}>
              <Typography variant="body2">{review.createdDateLabel}</Typography>
            </TableCell>

            <TableCell className={Styles.wrappingRowCell}>
              <Typography variant="body2">{review.reviewerName}</Typography>
            </TableCell>

            <TableCell className={Styles.wrappingRowCell}>
              <Typography variant="body2">{review.rating}</Typography>
            </TableCell>

            {/* '' for a review captured before GOTU-6521 shipped the field —
                an empty cell, not 'N/A'. The adapter owns that distinction. */}
            <TableCell className={Styles.wrappingRowCell}>
              <Typography variant="body2">{review.wouldWorkHereAgainLabel}</Typography>
            </TableCell>

            <TableCell className={`${Styles.wrappingRowCell} ${Styles.feedbackCell}`}>
              <Typography variant="body2">{review.publicFeedback}</Typography>
            </TableCell>

            <TableCell className={`${Styles.wrappingRowCell} ${Styles.feedbackCell}`}>
              <Typography variant="body2">{review.confidentialFeedback}</Typography>
            </TableCell>

            {/* Nothing at all when the review cannot be removed — past the
                delay, no parseable shift end, or no monolith id to delete
                against. One affordance for all three, per GOTU-7338. */}
            <TableCell className={Styles.wrappingRowCell}>
              {review.isRemovable ? (
                <IconButton
                  appearance="default"
                  color="secondary"
                  size="small"
                  // None of the four semantic colours is --text/primary, which
                  // is what the frame shows. `cn` uses tailwind-merge, so this
                  // replaces the variant's text colour rather than losing to it.
                  className="text-text-primary"
                  aria-label={`Remove review by ${review.reviewerName || 'this professional'}`}
                  Icon={<OutlineXIcon size={24} />}
                  onClick={() => onRemove(review)}
                />
              ) : null}
            </TableCell>
          </TableRow>
        ))}

        {/* The previous page's rows stay put while the next one loads or
            fails, so stepping never blanks the table and a failed page keeps
            something on screen to retry from. */}
        {isFetchingPage && (
          <StatusRow>
            <Spinner />
          </StatusRow>
        )}

        {hasFailedPage && (
          <StatusRow>
            <Typography variant="body2">Couldn&apos;t load reviews. Try again.</Typography>
            <Button variant="outlined" color="secondary" size="small" onClick={onRetry}>
              Retry
            </Button>
          </StatusRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

OfficeReviewsTable.propTypes = {
  /** Mapped rows, in the order organization-service returned them. */
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      monolithReviewId: PropTypes.string,
      jobId: PropTypes.string,
      shiftDateLabel: PropTypes.string,
      createdDateLabel: PropTypes.string,
      reviewerName: PropTypes.string,
      rating: PropTypes.number,
      wouldWorkHereAgainLabel: PropTypes.oneOf(['Yes', 'No', '']),
      publicFeedback: PropTypes.string,
      confidentialFeedback: PropTypes.string,
      isRemovable: PropTypes.bool,
    }),
  ).isRequired,
  /** Appends a spinner row beneath the previous page's rows while stepping. */
  isFetchingPage: PropTypes.bool,
  /** Appends a retry row beneath the loaded rows. */
  hasFailedPage: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  /** Called with the whole review, which the confirm dialog needs for its date. */
  onRemove: PropTypes.func.isRequired,
};

OfficeReviewsTable.defaultProps = {
  isFetchingPage: false,
  hasFailedPage: false,
};

export default OfficeReviewsTable;
